import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDisablePortalUserMutation } from "../../../../services/ProfileService";
import type { PortalUserView } from "../../../../services/ProfileService/types/portalUserTypes";

interface RemovePortalUserConfirmModalProps {
  open: boolean;
  onClose: () => void;
  user: PortalUserView | null;
  onSuccess: (message: string) => void;
}

export function RemovePortalUserConfirmModal({ open, onClose, user, onSuccess }: RemovePortalUserConfirmModalProps) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [disableUser, { isLoading }] = useDisablePortalUserMutation();

  const handleDelete = async () => {
    if (!user) return;
    setError(null);
    try {
      await disableUser({ oid: user.oid, practiceId: user.practiceId }).unwrap();
      handleClose();
      onSuccess(`${user.displayName} has been removed.`);
    } catch (err: any) {
      setError(err?.data?.message || err?.data || "Failed to remove user.");
    }
  };

  const handleClose = () => {
    setConfirmEmail("");
    setError(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove Portal User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning">
            This will permanently delete <strong>{user.displayName}</strong>&apos;s account.
            They will no longer be able to sign in and all access will be revoked.
          </Alert>
          <Typography variant="body2">
            Type <strong>{user.mail}</strong> to confirm:
          </Typography>
          <TextField
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={user.mail}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isLoading || confirmEmail !== user.mail}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? "Removing..." : "Delete Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
